import type { StudyMetadataResponse } from "./dicomMetadataLoader";

// Modality abbreviations that may already lead the descriptor (incl. Swedish
// "DT" = datortomografi = CT), so we don't prepend the modality twice.
const MODALITY_TOKENS = new Set([
  "CT",
  "DT",
  "MR",
  "MRI",
  "US",
  "XR",
  "NM",
  "PET",
  "MG",
  "CR",
  "DX",
  "RF",
  "BMD",
]);

/**
 * Approximate exam description assembled from per-series canonical metadata.
 *
 * The worklist payload has no DICOM StudyDescription, so the table lazily
 * fetches study metadata per visible row and builds "MODALITY BODYPART
 * [W CONTRAST]" from the series. This is a series-level approximation, NOT the
 * true StudyDescription (which would need a backend/ingest change).
 *
 * Rules:
 *  - modality = first series' modality; descriptor = most common body_part
 *    (fallback: first series_description);
 *  - don't double-prepend the modality when the descriptor already starts with
 *    a modality token (this cohort stuffs "CT …" / "DT …" into body_part);
 *  - append "W CONTRAST" only when a series has contrast AND the descriptor
 *    doesn't already mention contrast;
 *  - empty (no modality and no descriptor) → null.
 */
export function examLabelFromMetadata(meta: StudyMetadataResponse | undefined): string | null {
  const series = meta ? Object.values(meta.series_metadata ?? {}) : [];
  if (series.length === 0) return null;
  const modality = (series.find((s) => s.modality)?.modality ?? "").trim();
  const bodyCounts = new Map<string, number>();
  for (const s of series) {
    if (s.body_part) bodyCounts.set(s.body_part, (bodyCounts.get(s.body_part) ?? 0) + 1);
  }
  const descriptor = (
    [...bodyCounts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ??
    series.find((s) => s.series_description)?.series_description ??
    ""
  ).trim();
  if (!descriptor && !modality) return null;
  const hasContrast = series.some((s) => s.has_contrast);
  const firstTok = descriptor.toUpperCase().split(/[\s_/]+/)[0] ?? "";
  const base =
    !descriptor || MODALITY_TOKENS.has(firstTok) || !modality
      ? descriptor || modality
      : `${modality} ${descriptor}`;
  const label = hasContrast && !/contrast|kontrast/i.test(base) ? `${base} W CONTRAST` : base;
  return label.toUpperCase();
}
